import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RecurrencePicker } from './RecurrencePicker';
import { PreferredWindowField, windowKey, windowValue } from './PreferredWindowField';
import {
  TemplateMembers,
  membersFromItems,
  membersToItems,
  type MemberDraft,
} from './TemplateMembers';
import { QueryState } from './QueryState';
import {
  listTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  instantiateTemplate,
  getGoalTree,
  listHabits,
  ApiError,
  type Template,
  type TemplateDetail,
  type OnCreateMode,
  listLabels,
} from '../api';
import { Select } from './Select';
import { MultiSelect } from './MultiSelect';
import { goalOptions, renderGoalOption } from './pickerOptions';

const ON_CREATE_LABELS: Record<OnCreateMode, string> = {
  none: 'Just the task',
  ask: 'Task, and I pick a slot later',
  block_time: 'Task and a time block',
  reminders_only: 'Task and reminders, no block',
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function NewTemplateForm({ onDone }: { onDone: () => void }) {
  const goals = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const templates = useQuery({ queryKey: ['templates'], queryFn: listTemplates });
  const habits = useQuery({ queryKey: ['habits'], queryFn: listHabits });
  const [name, setName] = useState('');
  const [onCreate, setOnCreate] = useState<OnCreateMode>('none');
  const [goalId, setGoalId] = useState('');
  const [estimate, setEstimate] = useState('');
  const [steps, setSteps] = useState('');
  const [recurrenceRule, setRecurrenceRule] = useState('');
  const [habitId, setHabitId] = useState('');
  const [contribution, setContribution] = useState('1');
  const [members, setMembers] = useState<MemberDraft[]>([]);
  const [chain, setChain] = useState(true);
  const [window, setWindow] = useState('');
  /*
    Labelling a template labels everything it ever creates.

    38% of the owner's tasks come from templates, so seven templates labelled once categorises most
    of what they make at no per-task cost — the cheapest answer available to one task in twenty-one
    carrying any classification.
  */
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const allLabels = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  /*
    Personal labels only. The tasks a template makes are personal, and `createTemplate` refuses a
    project label for that reason (00486e0) — so offering one here was offering a refusal
    (`01a15b0d-4e1a`).
  */
  const labelPickerOptions = (allLabels.data ?? [])
    .filter((l) => l.projectId === null)
    .map((l) => ({ value: l.id, label: l.name }));

  /*
    A template is a composite when it has steps, not because a dropdown says so.

    `kind` is a two-value enum nobody outside the codebase has a word for. Adding a step is the
    thing somebody actually wants to do, so that is the control, and `kind` follows from it —
    which also means a composite can never be saved as `kind='bundle'` with nothing in it.
  */
  const kind = members.length > 0 ? 'bundle' : 'single';

  const create = useMutation({
    mutationFn: () =>
      createTemplate({
        name: name.trim(),
        onCreate,
        kind,
        chainMembers: chain,
        labelIds,
        ...(windowValue(window) ? { preferredWindows: windowValue(window)! } : {}),
        ...(goalId ? { goalId } : {}),
        ...(estimate ? { defaultEstimateMinutes: Number(estimate) } : {}),
        ...(habitId
          ? { linkedHabitId: habitId, habitContribution: Number(contribution) || 1 }
          : {}),
        ...(recurrenceRule ? { recurrenceRule } : {}),
        items: [
          ...steps
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean)
            .map((label) => ({ kind: 'checklist' as const, label })),
          ...membersToItems(members),
        ],
      }),
    onSuccess: onDone,
  });

  const habit = habits.data?.find((h) => h.id === habitId);
  // Bundles are excluded: nesting is forbidden at v1, so offering one would only produce a
  // validation error at save time.
  const memberCandidates = (templates.data ?? []).filter((t) => t.kind !== 'bundle');

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) create.mutate();
      }}
    >
      <h2>New template</h2>
      <div className="fields">
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Gym" />
        </label>
        <label>
          On create
          <select value={onCreate} onChange={(e) => setOnCreate(e.target.value as OnCreateMode)}>
            {(Object.keys(ON_CREATE_LABELS) as OnCreateMode[]).map((mode) => (
              <option key={mode} value={mode} disabled={mode === 'block_time'}>
                {ON_CREATE_LABELS[mode]}
                {mode === 'block_time' ? ' (Phase 4)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          Goal
          <Select
            options={goalOptions(goals.data ?? [])}
            value={goalId || null}
            onChange={(next) => setGoalId(next ?? '')}
            placeholder="None"
            ariaLabel="Goal"
            renderOption={renderGoalOption}
          />
        </label>
        <label>
          Estimate (min)
          <input
            type="number"
            min={1}
            value={estimate}
            onChange={(e) => setEstimate(e.target.value)}
          />
        </label>
        {/*
          The link that makes a template worth having for anything recurring: finishing
          the task *is* the habit entry, so there is no second thing to remember.
        */}
        <RecurrencePicker value={recurrenceRule} onChange={setRecurrenceRule} />
        <PreferredWindowField id="new-window" value={window} onChange={setWindow} />
        <label>
          Labels
          <MultiSelect
            options={labelPickerOptions}
            value={labelIds}
            onChange={setLabelIds}
            placeholder="None"
            ariaLabel="Labels"
            dialogTitle="Labels for this template"
          />
        </label>
        <label>
          Counts towards habit
          <select value={habitId} onChange={(e) => setHabitId(e.target.value)}>
            <option value="">None</option>
            {habits.data?.map((h) => (
              <option key={h.id} value={h.id}>
                {h.icon ? `${h.icon} ` : ''}
                {h.name}
              </option>
            ))}
          </select>
        </label>
        {habitId !== '' && (
          <label>
            Worth
            <input
              type="number"
              min={0.5}
              step="any"
              value={contribution}
              aria-label="Habit contribution"
              onChange={(e) => setContribution(e.target.value)}
            />
          </label>
        )}
      </div>

      {/*
        Spelled out rather than left to arithmetic. "Worth 2" is meaningless until you
        see it as two of the three the habit is asking for.
      */}
      {habit && (
        <p className="subtext" data-testid="contribution-explainer">
          Completing one of these logs {contribution || '1'}
          {habit.unit ? ` ${habit.unit}` : ''} towards “{habit.name}” — {contribution || '1'} of the{' '}
          {habit.targetCount}
          {habit.unit ? ` ${habit.unit}` : ''} you want per {habit.period}.
        </p>
      )}
      <label className="muted" htmlFor="steps">
        Checklist, one step per line
      </label>
      <textarea
        id="steps"
        rows={4}
        value={steps}
        onChange={(e) => setSteps(e.target.value)}
        placeholder={'Warm up\nMain sets\nStretch'}
      />

      <TemplateMembers
        idPrefix="new"
        members={members}
        onChange={setMembers}
        chain={chain}
        onChangeChain={setChain}
        candidates={memberCandidates}
      />

      {create.error instanceof ApiError && <p className="banner">{create.error.message}</p>}
      <div className="detail-foot">
        <button type="submit" className="primary" disabled={!name.trim() || create.isPending}>
          Create
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Editing a template.
 *
 * `updateTemplate` has existed in the API and the client since Phase 2 with no caller, so a
 * typo in a template could only be fixed by deleting it and starting again — which discards
 * `use_count` and `last_used_at`, the two things that make the most-recently-used ordering
 * work. The same shape as the habit gap: the service could always do this and the screen
 * could not.
 *
 * The care here is all in one place. The API replaces items *wholesale* when `items` is
 * present and keeps them when it is absent, so a form that sent an empty array while the user
 * was only fixing a name would silently delete the checklist. Every item is round-tripped.
 *
 * The `task` items used to be carried through blind, "because this form has no way to show
 * them" — which meant a bundle's members could be preserved but never seen or changed from the
 * app. They are editable now.
 */
function EditTemplateForm({ detail, onDone }: { detail: TemplateDetail; onDone: () => void }) {
  const queryClient = useQueryClient();
  const goals = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const habits = useQuery({ queryKey: ['habits'], queryFn: listHabits });
  const templates = useQuery({ queryKey: ['templates'], queryFn: listTemplates });
  const template = detail.template;

  const [name, setName] = useState(template.name);
  const [onCreate, setOnCreate] = useState<OnCreateMode>(template.onCreate);
  const [goalId, setGoalId] = useState(template.goalId ?? '');
  const [estimate, setEstimate] = useState(
    template.defaultEstimateMinutes === null ? '' : String(template.defaultEstimateMinutes),
  );
  const [habitId, setHabitId] = useState(template.linkedHabitId ?? '');
  const [contribution, setContribution] = useState(String(template.habitContribution));
  const [steps, setSteps] = useState(
    detail.items
      .filter((i) => i.kind === 'checklist')
      .map((i) => i.label)
      .join('\n'),
  );
  /*
    Editable here as well as on the create form.
    
    Shipped on the new-template form alone at first, which left every template that already
    existed unable to gain a recurrence without being deleted and retyped — losing its use count
    and its history. The templates most likely to want one are precisely the old ones you have
    been instantiating by hand for months.
  */
  const [recurrenceRule, setRecurrenceRule] = useState(template.recurrenceRule ?? '');

  const [members, setMembers] = useState<MemberDraft[]>(membersFromItems(detail.items));
  const [chain, setChain] = useState(template.chainMembers);
  const [window, setWindow] = useState(windowKey(template.preferredWindows));

  /*
    Project labels a template already carries, from before templates refused them (00486e0).

    Such a template cannot be instantiated — `instantiateTemplate` refuses it before making a task —
    and this form had no label control at all, so there was no way to take the label off
    (`01a15b0d-4e1a`). Only these are shown, not a full label picker: the job here is to clear the
    one thing standing in the way. A label not among the reader's personal ones is one of these
    whether or not they can still open its project, because every personal label is its owner's
    and the template is the reader's own.

    `labelIds` is sent only once something was removed, and then as everything that is left — the
    API replaces the set wholesale when named and keeps it when absent, the same contract as items,
    so a save that only fixes a name still leaves the labels alone.
  */
  const allLabels = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  const labelById = new Map((allLabels.data ?? []).map((l) => [l.id, l]));
  const [dropped, setDropped] = useState<string[]>([]);
  const projectLabelIds = allLabels.data
    ? template.labelIds.filter((id) => labelById.get(id)?.projectId !== null)
    : [];
  const strandedLabels = projectLabelIds.filter((id) => !dropped.includes(id));

  const save = useMutation({
    mutationFn: () =>
      updateTemplate(template.id, {
        name: name.trim(),
        onCreate,
        // Follows the steps, the same way it does on the create form: removing the last step
        // turns a composite back into a plain template rather than leaving an empty bundle.
        kind: members.length > 0 ? 'bundle' : 'single',
        chainMembers: chain,
        // `null` means the template carries a range this control cannot express — leave it be
        // rather than flattening somebody's 06:15–08:45 into "Morning".
        ...(windowValue(window) ? { preferredWindows: windowValue(window)! } : {}),
        goalId: goalId === '' ? null : goalId,
        defaultEstimateMinutes: estimate === '' ? null : Number(estimate),
        linkedHabitId: habitId === '' ? null : habitId,
        ...(habitId === '' ? {} : { habitContribution: Number(contribution) || 1 }),
        recurrenceRule: recurrenceRule === '' ? null : recurrenceRule,
        ...(dropped.length > 0
          ? { labelIds: template.labelIds.filter((id) => !dropped.includes(id)) }
          : {}),
        items: [
          ...steps
            .split('\n')
            .map((t) => t.trim())
            .filter(Boolean)
            .map((label) => ({ kind: 'checklist' as const, label })),
          ...membersToItems(members),
        ],
      }),
    onSuccess: () => {
      onDone();
      void queryClient.invalidateQueries();
    },
  });

  const habit = habits.data?.find((h) => h.id === habitId);
  // Bundles excluded (no nesting), and itself excluded — a template cannot contain itself.
  const memberCandidates = (templates.data ?? []).filter(
    (t) => t.kind !== 'bundle' && t.id !== template.id,
  );

  return (
    <form
      className="template-edit"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) save.mutate();
      }}
    >
      <div className="fields">
        <label>
          Name
          <input
            value={name}
            aria-label="Template name"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          On create
          <select
            value={onCreate}
            aria-label="On create"
            onChange={(e) => setOnCreate(e.target.value as OnCreateMode)}
          >
            {(Object.keys(ON_CREATE_LABELS) as OnCreateMode[]).map((mode) => (
              <option key={mode} value={mode} disabled={mode === 'block_time'}>
                {ON_CREATE_LABELS[mode]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Goal
          <Select
            options={goalOptions(goals.data ?? [])}
            value={goalId || null}
            onChange={(next) => setGoalId(next ?? '')}
            placeholder="None"
            ariaLabel="Goal"
            renderOption={renderGoalOption}
          />
        </label>
        <label>
          Estimate (min)
          <input
            type="number"
            min={1}
            value={estimate}
            aria-label="Estimate"
            onChange={(e) => setEstimate(e.target.value)}
          />
        </label>
        <RecurrencePicker value={recurrenceRule} onChange={setRecurrenceRule} />
        <PreferredWindowField id={`window-${template.id}`} value={window} onChange={setWindow} />
        <label>
          Counts towards habit
          <select
            value={habitId}
            aria-label="Counts towards habit"
            onChange={(e) => setHabitId(e.target.value)}
          >
            <option value="">None</option>
            {habits.data?.map((h) => (
              <option key={h.id} value={h.id}>
                {h.icon ? `${h.icon} ` : ''}
                {h.name}
              </option>
            ))}
          </select>
        </label>
        {habitId !== '' && (
          <label>
            Worth
            <input
              type="number"
              min={0.5}
              step="any"
              value={contribution}
              aria-label="Habit contribution"
              onChange={(e) => setContribution(e.target.value)}
            />
          </label>
        )}
      </div>

      {habit && (
        <p className="subtext">
          Completing one of these logs {contribution || '1'}
          {habit.unit ? ` ${habit.unit}` : ''} towards “{habit.name}”.
        </p>
      )}

      <label className="subtext" htmlFor={`steps-${template.id}`}>
        Checklist, one step per line
      </label>
      <textarea
        id={`steps-${template.id}`}
        rows={4}
        value={steps}
        aria-label="Checklist"
        onChange={(e) => setSteps(e.target.value)}
      />
      {strandedLabels.length > 0 && (
        <div className="template-project-labels">
          <p className="subtext">
            A template’s tasks are personal, so it cannot hand out a project label. Remove it to use
            this template again.
          </p>
          <ul>
            {strandedLabels.map((id) => {
              const name = labelById.get(id)?.name ?? 'A project label you can no longer open';
              return (
                <li key={id}>
                  <span>{name}</span>{' '}
                  <button
                    type="button"
                    className="secondary"
                    aria-label={`Remove label ${name}`}
                    onClick={() => setDropped((d) => [...d, id])}
                  >
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <TemplateMembers
        idPrefix={template.id}
        members={members}
        onChange={setMembers}
        chain={chain}
        onChangeChain={setChain}
        candidates={memberCandidates}
      />

      {save.error instanceof ApiError && <p className="banner">{save.error.message}</p>}

      <div className="detail-foot">
        <button type="submit" className="primary" disabled={!name.trim() || save.isPending}>
          Save
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function TemplateCard({
  template,
  onOpenTask,
}: {
  template: Template;
  onOpenTask: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const isBundle = template.kind === 'bundle';

  /*
    The detail this card already fetched for its variants, now fetched for a bundle before it
    is opened.

    A bundle's members live on `templateItems`, and the list endpoint returns the template row
    alone — so "this makes four tasks, here they are, in this order" is knowable only from
    `getTemplate`. The alternative was widening the list payload, which is a schema change on
    two services plus the client type for a fact only bundles have.

    `enabled` is what keeps that cheap. Most templates are singles and singles still fetch
    nothing until you open them, so a screen of ten templates with two bundles makes two
    requests, not ten. This does not reverse the lazy decision — it narrows it to the rows
    that have something to say.
  */
  const detail = useQuery({
    queryKey: ['template', template.id],
    queryFn: () => getTemplate(template.id),
    enabled: expanded || isBundle,
  });

  const members = isBundle ? (detail.data?.items.filter((i) => i.kind === 'task') ?? []) : [];
  /*
    A badge counts the noun it names, and this one sits beside the word "bundle".

    It read `bundle · 4 tasks` over three listed steps — 4 = the root plus its three members,
    because `instantiateTemplate` inserts the root first and hangs the children off it. That is a
    true sentence about what pressing the button *creates*, and it is not what a badge on a card
    listing three steps reads as: "this bundle contains four of the things below". The prose
    underneath already explains the +1 ("plus one task per step"), so the badge does not have to.

    So it counts steps, which is the noun rendered beneath it. Same rule the board's column heading
    now follows.
  */

  /*
    Read, not fetched: this is the same `['templates']` query the list around us is already
    rendering from, so resolving a member costs nothing.

    Worth resolving because instantiating falls back the same way — a member with no estimate
    of its own is created with its template's default — and an estimate the card invents or
    omits would be a lie about the tasks you are about to get.
  */
  const templatesById = new Map(
    (isBundle ? (queryClient.getQueryData<Template[]>(['templates']) ?? []) : []).map(
      (t) => [t.id, t] as const,
    ),
  );

  const run = useMutation({
    mutationFn: (variant?: string) => instantiateTemplate(template.id, variant ? { variant } : {}),
    onSuccess: (result) => {
      const created = result.tasks[0];
      const parts = [`Created “${created?.title}”`];
      if (result.reminderIds.length) {
        parts.push(`with ${result.reminderIds.length} reminder(s)`);
      }
      if (result.onCreate === 'ask') parts.push('— pick a slot when you know the day');
      if (result.tasks.length > 1) parts.push(`and ${result.tasks.length - 1} linked task(s)`);
      setMessage(parts.join(' '));
      void queryClient.invalidateQueries();
    },
    onError: (e) => setMessage(e instanceof ApiError ? e.message : 'Something went wrong.'),
  });

  const remove = useMutation({
    mutationFn: () => deleteTemplate(template.id),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  return (
    <li className="card template-card">
      <div className="template-head">
        <button className="link template-name" onClick={() => setExpanded((v) => !v)}>
          {template.icon ? `${template.icon} ` : ''}
          {template.name}
        </button>
        <span className="task-meta">
          {/*
            The badge row is where this card already says what a template is, so this is said
            there too rather than in a second strip of its own. The count arrives with the
            detail; until then the badge is honest about what it knows.
          */}
          {isBundle && (
            <span className="pill" data-testid="bundle-pill">
              {/* `0 steps` once the detail has loaded, so every bundle has one badge shape; the
                  bare word only while it is still in flight. A bundle with no steps is a real
                  state and used to be the one card that said nothing at all. */}
              {detail.data ? `bundle · ${members.length} steps` : 'bundle'}
            </span>
          )}
          {isBundle && (
            <span className="pill" data-testid="chain-pill">
              {template.chainMembers ? 'chained' : 'independent'}
            </span>
          )}
          {/*
            Only when it is something other than the default. "Just the task" is a fact about
            scheduling — no block, no reminders — but read as a count it contradicts the
            "bundle · 4 tasks" pill two places to its left, and on a single it repeats the
            default back at you on every card. The three modes that do schedule something,
            which are the ones worth knowing before you press the button, still say so.
          */}
          {template.onCreate !== 'none' && (
            <span className="pill">{ON_CREATE_LABELS[template.onCreate]}</span>
          )}
          {template.recurrenceText && <span className="pill">{template.recurrenceText}</span>}
          {template.defaultEstimateMinutes && (
            <span className="pill">{template.defaultEstimateMinutes}m</span>
          )}
          {template.linkedHabitId && (
            <span className="pill ok" title="Completing one of these logs a habit entry">
              habit ×{template.habitContribution}
            </span>
          )}
          <span className="pill">used {template.useCount}×</span>
        </span>
      </div>

      {/*
        What "bundle" and "chained" actually mean, once, underneath the badges that use the
        words. Four independent tasks and four that queue behind each other are different days,
        and the difference was previously discoverable only by pressing the button.

        Singles render none of this and gain no chrome — they have nothing to say here.
      */}
      {isBundle && members.length > 0 && (
        <div data-testid="bundle-steps">
          <p className="subtext">
            “{template.name}” plus one task per step,{' '}
            {template.chainMembers
              ? 'each blocked until the step before it is done'
              : 'all free to start at once'}
            .
          </p>
          {/*
            `member-list` is the edit form's own list of these same members, and it already
            lays out exactly this row — name flexed, pills trailing. A second class describing
            the same list differently is the thing worth not having.
          */}
          <ol className="plain member-list">
            {members.map((item) => {
              const member = item.memberTemplateId
                ? templatesById.get(item.memberTemplateId)
                : undefined;
              // The title the child task gets comes from the member template, so a renamed
              // member is shown under its current name rather than the label frozen at the
              // moment it was added.
              const estimate = item.estimateMinutes ?? member?.defaultEstimateMinutes ?? null;
              return (
                <li key={item.id} data-testid="bundle-step">
                  <span>{member?.name ?? item.label}</span>
                  {/* Same snapshot case the edit form names, in the same words: the member's
                      template is gone, so this step arrives as a bare task. */}
                  {item.memberTemplateId === null && (
                    <span className="pill warn">template gone</span>
                  )}
                  {estimate !== null && <span className="pill">{estimate}m</span>}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {template.preferredWeekdays.length > 0 && (
        <p className="muted">
          Usually {template.preferredWeekdays.map((d) => WEEKDAYS[d]).join(', ')} — using this
          without naming a day plans it for the next one. Still a hint: name a day and that wins.
        </p>
      )}

      <div className="template-actions">
        <button onClick={() => run.mutate(undefined)} disabled={run.isPending}>
          Use template
        </button>
        {detail.data?.variants.map((v) => (
          <button key={v.id} onClick={() => run.mutate(v.name)} disabled={run.isPending}>
            {v.name}
          </button>
        ))}
        <button
          className="link danger"
          onClick={() => {
            if (confirm(`Delete the "${template.name}" template? Tasks it made are kept.`))
              remove.mutate();
          }}
        >
          delete
        </button>
      </div>

      {message && (
        <p className="banner ok-banner">
          {message}{' '}
          {run.data?.tasks[0] && (
            <button className="link" onClick={() => onOpenTask(run.data.tasks[0]!.id)}>
              open
            </button>
          )}
        </p>
      )}

      {expanded && (
        <div className="panel">
          <QueryState query={detail} />
          {detail.data && editing && (
            <EditTemplateForm detail={detail.data} onDone={() => setEditing(false)} />
          )}
          {detail.data && !editing && (
            <>
              <div className="detail-foot">
                <button onClick={() => setEditing(true)}>Edit</button>
              </div>
              <p className="muted">Title pattern: {detail.data.template.titlePattern}</p>
              {/*
                Checklist only. The `task` items are the bundle's members, and the head of the
                card now lists those in order with their estimates — repeating them here under
                a `task` pill would be the same fact twice, told worse the second time.
              */}
              {detail.data.items.some((i) => i.kind === 'checklist') && (
                <ul>
                  {detail.data.items
                    .filter((i) => i.kind === 'checklist')
                    .map((i) => (
                      <li key={i.id}>{i.label}</li>
                    ))}
                </ul>
              )}
              {detail.data.variants.length > 0 && (
                <p className="muted">
                  Variants: {detail.data.variants.map((v) => v.name).join(', ')}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function Templates({ onOpenTask }: { onOpenTask: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const templates = useQuery({ queryKey: ['templates'], queryFn: listTemplates });

  return (
    <section>
      <div className="capture">
        {/*
          The disclosure disappears while the form is open rather than relabelling itself `Cancel`.

          It used to become a second Cancel, so one form offered two ways out — a floating one above
          the card and the form's own, beside the commit. The reviewer photographed it on 1.39.0 and
          the same pair had already been filed on 1.10.0 (`01a0809a-3781`). Two dismiss controls and
          one commit control is the wrong count in the wrong direction, and the floating one is the
          one a reader has no reason to look for.

          Hidden, not disabled: a disabled control still occupies the eye and still has to explain
          itself. The form's own Cancel is the way out, and it sits where the decision is being made.
        */}
        {!adding && <button onClick={() => setAdding(true)}>New template</button>}
      </div>

      {adding && (
        <NewTemplateForm
          onDone={() => {
            setAdding(false);
            void queryClient.invalidateQueries({ queryKey: ['templates'] });
          }}
        />
      )}

      <QueryState query={templates} />
      {templates.data?.length === 0 && !adding && (
        <p className="muted">
          No templates yet. A template is for work you repeat in intent but not on a fixed day — a
          gym session, a trip-prep bundle, the monthly rent reminder.
        </p>
      )}

      <ul className="template-list">
        {templates.data?.map((template) => (
          <TemplateCard key={template.id} template={template} onOpenTask={onOpenTask} />
        ))}
      </ul>
    </section>
  );
}
